import {SelectComponent, SelectOption, SelectProps} from "@/components/select/Select.types.ts";
import {useMultiSelectSearchPersistence} from "@/components/select/useMultiSelectSearchPersistence.ts";
import classNames from "classnames";
import {ForwardedRef, forwardRef, useCallback, useImperativeHandle, useRef} from "react";
import ReactSelect, {ControlProps, GroupBase, SelectInstance} from "react-select";
import AsyncSelect from "react-select/async";
import CreatableSelect from "react-select/creatable";
import styles from "./Select.module.scss";
import {ClearIndicator} from "./subComponents/ClearIndicator/ClearIndicator";
import {Control} from "./subComponents/Control/Control";
import {DropdownIndicator} from "./subComponents/DropdownIndicator/DropdownIndicator";
import {MultiValueRemove} from "./subComponents/MultiValueRemove/MultiValueRemove";

//Notes:
// 1. I can't use the uptime Input component in react-select to replace its input because the complexity of how react-select generates its input is very different to account for things like multi select
// 2. I will be using scss to style the select component as all of our variables for styling are already in scss, and it doesn't make sense to convert them to css-in-js just for this component.

export const Select = forwardRef(
  <Option = SelectOption, IsMulti extends boolean = false, Group extends GroupBase<Option> = GroupBase<Option>>(
    {
      className,
      size = "small",
      variant = "default",
      async,
      creatable,
      leftIcon,
      components,
      isClearable,
      maxSelections,
      isMulti,
      inputValue,
      onInputChange,
      onChange,
      onCreateOption,
      ...props
    }: SelectProps<Option, IsMulti, Group>,
    ref: ForwardedRef<SelectInstance<Option, IsMulti, Group>>
  ) => {
    const Select = async ? AsyncSelect : creatable ? CreatableSelect : ReactSelect;

    const innerRef = useRef<SelectInstance<Option, IsMulti, Group>>(null);
    useImperativeHandle(ref, () => innerRef.current as SelectInstance<Option, IsMulti, Group>);

    const searchPersistence = useMultiSelectSearchPersistence({
      isMulti,
      selectRef: innerRef,
      inputValue,
      onInputChange,
      onChange,
      onCreateOption,
    });

    const selectedCount = isMulti && Array.isArray(props.value) ? props.value.length : 0;
    const isAtMax = isMulti && maxSelections !== undefined && selectedCount >= maxSelections;

    const isOptionDisabled = isAtMax
      ? (option: Option) => {
          const value = (props.value as unknown as Option[]) ?? [];
          const getVal = props.getOptionValue ?? ((o: Option) => (o as {value: string}).value);
          return !value.some((v) => getVal(v) === getVal(option));
        }
      : props.isOptionDisabled;
    // This is used to memoize the control so it doesn't rerender any time the options are loaded which causes the input to lose focus
    // With this memoized users can continue to type as options load
    const MemoizedControl = useCallback(
      ({children, ...props}: ControlProps<Option, IsMulti, Group>) => (
        <Control leftIcon={leftIcon} {...props}>
          {children}
        </Control>
      ),
      [leftIcon]
    );
    return (
      <Select
        ref={innerRef}
        closeMenuOnSelect={!isMulti}
        classNames={{
          container: () => classNames(styles.select, styles[size], styles[variant], className),
          control: (state) =>
            classNames(
              styles.selectControl,
              {[styles["--is-focused"]]: state.isFocused},
              {[styles["--is-disabled"]]: state.isDisabled},
              {[styles["--is-error"]]: props["aria-invalid"]}
            ),
          placeholder: () => styles.selectPlaceholder,
          singleValue: (state) =>
            classNames(
              styles.selectSingleValue,
              {[styles["--is-disabled"]]: state.isDisabled},
              {[styles["--is-error"]]: props["aria-invalid"]}
            ),
          valueContainer: () => classNames(styles.selectValueContainer, {[styles["--has-left-icon"]]: !!leftIcon}),
          clearIndicator: () =>
            classNames(styles.selectClearIndicator, {[styles["--is-error"]]: props["aria-invalid"]}),
          indicatorSeparator: () =>
            classNames(styles.selectIndicatorSeparator, {[styles["--is-error"]]: props["aria-invalid"]}),
          dropdownIndicator: (state) =>
            classNames(
              styles.dropDownIndicator,
              {[styles["--is-disabled"]]: state.isDisabled},
              {[styles["--is-error"]]: props["aria-invalid"]}
            ),
          menu: () => styles.selectMenu,
          menuPortal: () => classNames(styles.select, styles.portalMenu),
          menuList: () => styles.selectMenuList,
          option: (state) =>
            classNames(
              styles.selectOption,
              {[styles["--is-focused"]]: state.isFocused},
              {[styles["--is-selected"]]: state.isSelected},
              {[styles["--is-error"]]: props["aria-invalid"]}
            ),
          groupHeading: () => styles.selectGroupHeading,
          group: () => styles.selectGroup,
          multiValue: () => styles.selectMultiValue,
          multiValueLabel: () => styles.selectMultiValueLabel,
          multiValueRemove: (state) =>
            classNames(styles.selectMultiValueRemove, {[styles["--is-disabled"]]: state.isDisabled}),
        }}
        isClearable={isClearable}
        isOptionDisabled={isOptionDisabled}
        components={{
          DropdownIndicator,
          ClearIndicator: (props) => isClearable && ClearIndicator<Option, IsMulti, Group>(props),
          Control: MemoizedControl,
          MultiValueRemove,
          ...components,
        }}
        isMulti={isMulti}
        {...props}
        {...searchPersistence}
      />
    );
  }
) as SelectComponent;
